/**
 * Rodina / Family management — see WHO is in the family and control WHAT each
 * person sees. Parents hold every scope ("Plný prístup"); trusted people (e.g. a
 * grandparent) see only the scopes you grant them, sensitive ones clearly flagged.
 *
 * Mirrors @solandia/core/permissions and is parent-gated: only parents can rename
 * the family, invite members, edit a trusted member's access or remove anyone.
 */
import { useMemo, useState } from 'react';
import type { Family, MemberRole, Membership, Scope } from '@solandia/core';
import {
  ALL_SCOPES,
  canManageFamily,
  inviteMemberInputSchema,
  isSensitiveScope,
  normalizeScopes,
  updateFamilyInputSchema,
} from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import {
  Badge,
  Dialog,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';

/** First grapheme of a display label, used as an avatar fallback. */
function initialOf(label: string): string {
  const trimmed = label.trim();
  return trimmed ? trimmed[0]!.toUpperCase() : '?';
}

export function SharingModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, me, membership, reloadMe } = useFamily();
  const canManage = canManageFamily(membership);

  const { data, loading, error, reload } = useAsync<Membership[]>(async () => {
    const res = await api.listMembers(family.id);
    return res.items;
  }, [family.id]);

  const [inviting, setInviting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [editing, setEditing] = useState<Membership | null>(null);

  async function remove(m: Membership) {
    if (!window.confirm(t.sharing.removeConfirm)) return;
    await api.removeMember(family.id, m.id);
    reload();
  }

  return (
    <section>
      <SectionHeader
        title={t.sharing.title}
        subtitle={t.sharing.lead}
        action={
          canManage ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setInviting(true)}
            >
              {t.sharing.inviteMember}
            </button>
          ) : undefined
        }
      />

      {/* Family name header — read-only for trusted, inline-rename for parents. */}
      <div className="family-header card card-tight">
        <div className="grow stack-sm">
          <span className="text-xs muted">{t.sharing.familyNameLabel}</span>
          <strong className="family-name">{family.name}</strong>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setRenaming(true)}
          >
            {t.sharing.rename}
          </button>
        ) : null}
      </div>

      {!canManage ? (
        <p className="banner banner-info" role="note">
          <span aria-hidden="true">ℹ️</span>
          <span className="grow">{t.sharing.onlyParents}</span>
        </p>
      ) : null}

      <h3 className="section-subtitle" style={{ marginTop: 'var(--sol-space-lg)' }}>
        {t.sharing.members}
      </h3>

      {loading ? <Spinner label={t.common.loading} /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}

      {data && data.length > 0 ? (
        <ul className="member-list" aria-label={t.sharing.members}>
          {data.map((m) => (
            <MemberCard
              key={m.id}
              membership={m}
              isSelf={m.userId === me.user.id}
              canManage={canManage}
              onEdit={() => setEditing(m)}
              onRemove={() => remove(m)}
            />
          ))}
        </ul>
      ) : null}

      {inviting ? (
        <InviteDialog
          onClose={() => setInviting(false)}
          onDone={() => {
            setInviting(false);
            reload();
          }}
        />
      ) : null}

      {renaming ? (
        <RenameDialog
          family={family}
          onClose={() => setRenaming(false)}
          onDone={() => {
            setRenaming(false);
            reloadMe();
          }}
        />
      ) : null}

      {editing ? (
        <ScopesDialog
          membership={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function MemberCard({
  membership,
  isSelf,
  canManage,
  onEdit,
  onRemove,
}: {
  membership: Membership;
  isSelf: boolean;
  canManage: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  const isParent = membership.role === 'parent';
  const roleLabel = isParent ? t.sharing.parentBadge : t.sharing.trustedBadge;
  // What this member can see: a parent has everything; a trusted member only
  // their granted scopes.
  const scopes = isParent ? ALL_SCOPES : membership.permissions;

  return (
    <li className="member-card">
      <span className="member-avatar" aria-hidden="true">
        {initialOf(roleLabel)}
      </span>

      <div className="grow stack-sm">
        <div className="row wrap">
          <strong>
            {roleLabel}
            {isSelf ? (
              <span className="member-you"> · {t.sharing.you}</span>
            ) : null}
          </strong>
          <Badge tone={isParent ? 'primary' : 'default'}>{roleLabel}</Badge>
          <Badge tone={membership.status === 'active' ? 'success' : 'accent'}>
            {t.sharing.status[membership.status]}
          </Badge>
        </div>

        <div className="stack-sm">
          <span className="text-xs muted">{t.sharing.seesLabel}</span>
          {isParent ? (
            <span className="member-fullaccess">
              <Badge tone="primary">{t.sharing.fullAccess}</Badge>
            </span>
          ) : scopes.length > 0 ? (
            <div className="row wrap" style={{ gap: 6 }}>
              {scopes.map((s) => (
                <Badge key={s} tone={isSensitiveScope(s) ? 'accent' : 'default'}>
                  {t.scopes[s]}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-sm muted">{t.sharing.seesNothing}</span>
          )}
        </div>
      </div>

      {canManage && !isSelf ? (
        <div className="member-actions stack-sm">
          {!isParent ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
              {t.sharing.changeScopes}
            </button>
          ) : null}
          <button type="button" className="btn btn-danger btn-sm" onClick={onRemove}>
            {t.sharing.removeMember}
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Friendly role picker: two cards (Rodič / Dôveryhodná osoba) with a short
 * explanation each. Selecting trusted reveals the scope checklist.
 */
function RolePicker({
  role,
  onChange,
}: {
  role: MemberRole;
  onChange: (role: MemberRole) => void;
}) {
  const { t } = useI18n();
  const options: Array<{ value: MemberRole; title: string; desc: string }> = [
    { value: 'trusted', title: t.sharing.roleTrusted, desc: t.sharing.roleTrustedDesc },
    { value: 'parent', title: t.sharing.roleParent, desc: t.sharing.roleParentDesc },
  ];
  return (
    <div className="field">
      <span className="field-label" id="role-picker-label">
        {t.sharing.roleLabel}
      </span>
      <div
        className="role-picker"
        role="radiogroup"
        aria-labelledby="role-picker-label"
      >
        {options.map((opt) => {
          const selected = role === opt.value;
          return (
            <label
              key={opt.value}
              className={selected ? 'role-option selected' : 'role-option'}
            >
              <input
                type="radio"
                name="member-role"
                value={opt.value}
                checked={selected}
                onChange={() => onChange(opt.value)}
              />
              <span className="stack-sm grow">
                <strong>{opt.title}</strong>
                <span className="text-sm muted">{opt.desc}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ScopeChecklist({
  role,
  selected,
  onToggle,
}: {
  role: MemberRole;
  selected: Set<Scope>;
  onToggle: (scope: Scope, on: boolean) => void;
}) {
  const { t } = useI18n();
  const disabled = role === 'parent';
  return (
    <div className="scope-list">
      {ALL_SCOPES.map((s) => {
        const sensitive = isSensitiveScope(s);
        return (
          <label
            key={s}
            className={sensitive ? 'scope-toggle sensitive' : 'scope-toggle'}
          >
            <input
              type="checkbox"
              checked={disabled || selected.has(s)}
              disabled={disabled}
              onChange={(e) => onToggle(s, e.target.checked)}
            />
            <span className="grow">
              {t.scopes[s]}
              {sensitive ? (
                <span className="text-xs muted" style={{ display: 'block' }}>
                  {t.sharing.sensitiveNote}
                </span>
              ) : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function RenameDialog({
  family,
  onClose,
  onDone,
}: {
  family: Family;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const [name, setName] = useState(family.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const parsed = updateFamilyInputSchema.safeParse({ name: name.trim() });
    if (!parsed.success) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateFamily(family.id, parsed.data);
      onDone();
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.sharing.renameTitle} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        {error ? <ErrorState message={error} /> : null}
        <Field label={t.sharing.familyNameLabel}>
          {({ id, invalid }) => (
            <input
              id={id}
              type="text"
              className="input"
              value={name}
              aria-invalid={invalid}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.family.namePlaceholder}
              autoFocus
            />
          )}
        </Field>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.save}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}

function InviteDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('trusted');
  const [selected, setSelected] = useState<Set<Scope>>(
    new Set<Scope>(['calendar', 'contacts']),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  function toggle(scope: Scope, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(scope);
      else next.delete(scope);
      return next;
    });
  }

  async function submit() {
    const permissions = normalizeScopes(role, [...selected]);
    const input = {
      email: email.trim(),
      role,
      permissions: role === 'parent' ? undefined : permissions,
    };
    const parsed = inviteMemberInputSchema.safeParse(input);
    if (!parsed.success) {
      setErrors({ email: t.common.required });
      return;
    }
    setBusy(true);
    try {
      await api.inviteMember(family.id, parsed.data);
      onDone();
    } catch {
      setErrors({ email: t.errors.generic });
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.sharing.inviteTitle} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <Field label={t.sharing.emailLabel} error={errors.email}>
          {({ id, invalid }) => (
            <input
              id={id}
              type="email"
              className="input"
              value={email}
              aria-invalid={invalid}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="meno@email.sk"
              autoFocus
            />
          )}
        </Field>

        <RolePicker role={role} onChange={setRole} />

        {role === 'trusted' ? (
          <div className="field">
            <span className="field-label">{t.sharing.scopesLabel}</span>
            <span className="field-hint">{t.sharing.scopesHint}</span>
            <div style={{ marginTop: 8 }}>
              <ScopeChecklist role={role} selected={selected} onToggle={toggle} />
            </div>
          </div>
        ) : (
          <p className="banner banner-info" role="note">
            <span aria-hidden="true">ℹ️</span>
            <span className="grow">{t.sharing.parentScopesNote}</span>
          </p>
        )}

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.sharing.invite}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}

function ScopesDialog({
  membership,
  onClose,
  onDone,
}: {
  membership: Membership;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const [selected, setSelected] = useState<Set<Scope>>(
    () => new Set(membership.permissions),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const normalized = useMemo(
    () => normalizeScopes('trusted', [...selected]),
    [selected],
  );

  function toggle(scope: Scope, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(scope);
      else next.delete(scope);
      return next;
    });
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api.updateMember(family.id, membership.id, { permissions: normalized });
      onDone();
    } catch {
      setErr(t.errors.generic);
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.sharing.changeScopes} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        {err ? <ErrorState message={err} /> : null}
        <span className="field-hint">{t.sharing.scopesHint}</span>
        <ScopeChecklist role="trusted" selected={selected} onToggle={toggle} />
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.save}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
